import {
  isJourneySnapshot,
  JOURNEY_TTL_MS,
  type JourneySnapshot,
} from "./quoteJourneyState";

const DATABASE = "quickquote-journey";
const STORE = "snapshots";
const STORAGE_WAIT_MS = 5000;

function openDatabase(factory: () => IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let finished = false;
    const timer = setTimeout(() => fail(), STORAGE_WAIT_MS);
    const fail = () => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      reject(new Error("未能打开本地工作暂存"));
    };
    try {
      const request = factory().open(DATABASE, 1);
      request.onupgradeneeded = () => {
        const store = request.result.createObjectStore(STORE, {
          keyPath: "id",
        });
        store.createIndex("scope", "scope");
      };
      request.onerror = fail;
      request.onblocked = fail;
      request.onsuccess = () => {
        if (finished) {
          request.result.close();
          return;
        }
        finished = true;
        clearTimeout(timer);
        resolve(request.result);
      };
    } catch {
      fail();
    }
  });
}

/** 必须等transaction.complete，不以put/get的request.success冒充完整事务成功。 */
async function transact<T>(
  factory: () => IDBFactory,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore, result: (value: T) => void) => void,
): Promise<T> {
  const db = await openDatabase(factory);
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      let value: T;
      let settled = false;
      const fail = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(new Error("未能完整保存或读取本地工作"));
      };
      const timer = setTimeout(() => {
        try {
          tx.abort();
        } catch {
          /* 已结束的事务也不能报告成功。 */
        }
        fail();
      }, STORAGE_WAIT_MS);
      tx.onabort = fail;
      tx.onerror = fail;
      tx.oncomplete = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(value);
      };
      try {
        run(tx.objectStore(STORE), (next) => {
          value = next;
        });
      } catch {
        try {
          tx.abort();
        } finally {
          fail();
        }
      }
    });
  } finally {
    db.close();
  }
}

export function createQuoteJourneyStore(
  factory: () => IDBFactory = () => globalThis.indexedDB,
) {
  const store = {
    async save(input: JourneySnapshot): Promise<void> {
      const snapshot = structuredClone(input);
      const now = Date.now();
      if (!isJourneySnapshot(snapshot, snapshot?.scope, now))
        throw new Error("本地工作信息无效，请留在当前页面");
      await transact<void>(factory, "readwrite", (table) => {
        const existing = table.get(snapshot.id);
        existing.onsuccess = () => {
          if (existing.result && existing.result.scope !== snapshot.scope) {
            table.transaction.abort();
            return;
          }
          try {
            table.put(snapshot);
          } catch {
            table.transaction.abort();
          }
        };
        const cursor = table.openCursor();
        cursor.onsuccess = () => {
          const row = cursor.result;
          if (!row) return;
          const createdAt = row.value?.createdAt;
          if (
            row.key !== snapshot.id &&
            typeof createdAt === "number" &&
            now - createdAt >= JOURNEY_TTL_MS
          )
            row.delete();
          row.continue();
        };
      });
    },
    async load(
      id: string,
      scope: string,
      now = Date.now(),
    ): Promise<JourneySnapshot | null> {
      const value = await transact<unknown>(
        factory,
        "readwrite",
        (table, result) => {
          const request = table.get(id);
          request.onsuccess = () => {
            const got = request.result;
            if (
              got?.scope === scope &&
              typeof got.createdAt === "number" &&
              now - got.createdAt >= JOURNEY_TTL_MS
            ) {
              table.delete(id);
              result(null);
            } else result(got);
          };
        },
      );
      return isJourneySnapshot(value, scope, now) ? value : null;
    },
    async delete(id: string, scope?: string): Promise<void> {
      await transact<void>(factory, "readwrite", (table) => {
        if (scope === undefined) {
          table.delete(id);
          return;
        }
        const request = table.get(id);
        request.onsuccess = () => {
          if (request.result?.scope === scope) table.delete(id);
        };
      });
    },
    async clearScope(scope: string): Promise<void> {
      await transact<void>(factory, "readwrite", (table) => {
        const cursor = table.index("scope").openCursor(scope);
        cursor.onsuccess = () => {
          const row = cursor.result;
          if (!row) return;
          row.delete();
          row.continue();
        };
      });
    },
  };
  return store;
}
